"use client";

import Image from "next/image";
import { ArrowRight, ImageIcon } from "lucide-react";
import { CtaButton } from "@/components/layout/CtaButton";

export type AnimatedBookCardProps = {
  imageSrc: string;
  imageAlt: string;
  title: string;
  author?: string;
  exploreHref?: string;
  exploreLabel?: string;
  /** When true, Explore More is non-interactive (no detail page available). */
  exploreDisabled?: boolean;
  /** Compact size for embedding in testimonial card footers */
  size?: "md" | "sm";
  className?: string;
};

export function AnimatedBookCard({
  imageSrc,
  imageAlt,
  title,
  author,
  exploreHref = "#lead",
  exploreLabel = "Explore More",
  exploreDisabled = false,
  size = "md",
  className,
}: AnimatedBookCardProps) {
  const isSm = size === "sm";
  const sizes = isSm
    ? "72px"
    : "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 280px";

  return (
    <div
      className={[
        "animated-book-container group",
        isSm ? "animated-book-container--sm" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      tabIndex={0}
      role="group"
      aria-label={`${title}${author ? ` by ${author}` : ""}`}
    >
      <div className="animated-book" aria-hidden="false">
        <div className="animated-book-cover">
          <div className="animated-book-cover-front">
            {imageSrc ? (
              <Image
                src={imageSrc}
                alt={imageAlt}
                fill
                sizes={sizes}
                quality={50}
                loading="lazy"
                fetchPriority="low"
                className="object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-[#1f2914]">
                <ImageIcon
                  className={isSm ? "h-4 w-4 text-white/40" : "h-10 w-10 text-white/40"}
                  aria-hidden="true"
                />
              </div>
            )}
          </div>
          {/*
            Inside cover: skip a second Image download for compact cards (dark
            overlay hides detail). Full-size cards keep a single optimized copy.
          */}
          <div className="animated-book-cover-inside" aria-hidden="true">
            {imageSrc && !isSm ? (
              <Image
                src={imageSrc}
                alt=""
                fill
                sizes={sizes}
                quality={50}
                loading="lazy"
                fetchPriority="low"
                className="object-cover"
              />
            ) : null}
          </div>
        </div>

        <div className="animated-book-thickness" aria-hidden="true" />

        <div className="animated-book-page">
          <div className="animated-book-page-header">
            <h3 className="animated-book-page-title">{title}</h3>
          </div>

          {author ? (
            <div className="animated-book-meta">
              <span>
                <strong>Author:</strong> {author}
              </span>
            </div>
          ) : null}

          {!isSm ? (
            <div className="animated-book-page-cta">
              {exploreDisabled ? (
                <CtaButton
                  variant="green"
                  size="sm"
                  icon={<ArrowRight className="h-3.5 w-3.5" />}
                  iconPosition="right"
                  disabled
                  className="w-full cursor-not-allowed justify-center opacity-50"
                >
                  {exploreLabel}
                </CtaButton>
              ) : (
                <CtaButton
                  href={exploreHref}
                  variant="green"
                  size="sm"
                  icon={<ArrowRight className="h-3.5 w-3.5" />}
                  iconPosition="right"
                  className="w-full justify-center"
                >
                  {exploreLabel}
                </CtaButton>
              )}
            </div>
          ) : null}
        </div>

        <div className="animated-book-back" aria-hidden="true" />
      </div>
    </div>
  );
}
